import * as Phaser from "phaser"
import type { EnemyMovementSpec } from "../../data/enemies"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

type PiranhaPhase = "swim" | "focus" | "charge" | "retreat" | "exit"

export class PiranhaEnemy extends StatefulEnemyRuntime<PiranhaPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<PiranhaPhase>[] = [
      {
        name: "swim",
        behaviorState: "flying",
        animationState: "flying",
        enter: (context) => this.playPiranhaAnimation(context),
        update: (context) => this.updateSwim(context),
      },
      {
        name: "focus",
        behaviorState: "focusing",
        animationState: "focusing",
        enter: (context) => this.playPiranhaAnimation(context),
        update: (context) => this.updateFocus(context),
      },
      {
        name: "charge",
        behaviorState: "diving",
        animationState: "diving",
        enter: (context) => this.playPiranhaAnimation(context),
        update: (context) => this.updateCharge(context),
      },
      {
        name: "retreat",
        behaviorState: "retreating",
        animationState: "retreating",
        enter: (context) => this.playPiranhaAnimation(context),
        update: (context) => this.updateRetreat(context),
      },
      {
        name: "exit",
        behaviorState: "flying",
        animationState: "flying",
        enter: (context) => this.playPiranhaAnimation(context),
        update: (context) => this.updateExit(context),
      },
    ]

    super(sprite, enemyData, "swim", states)
  }

  private playPiranhaAnimation(context: EnemyStateContext<PiranhaPhase>): void {
    if (context.scene.anims.exists("anim_piranha")) {
      context.sprite.play("anim_piranha", true)
    }
  }

  private getStrikeSpec(
    context: EnemyStateContext<PiranhaPhase>,
  ): Extract<EnemyMovementSpec, { action: "piranha-strike" }> | null {
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "piranha-strike") return null

    return spec
  }

  private updateSwim(
    context: EnemyStateContext<PiranhaPhase>,
  ): PiranhaPhase | null {
    const { sprite, enemyData, time, delta, scrollCompensation } = context
    const homeX = enemyData.homeX ?? sprite.x
    const homeY = enemyData.homeY ?? sprite.y
    const spec = this.getStrikeSpec(context)
    if (!spec) return null

    enemyData.vx = scrollCompensation
    enemyData.vy = 0
    sprite.x -= 2.5 * (delta / 16)
    const wave = Math.sin(time / 250) * 30
    sprite.y = Phaser.Math.Linear(sprite.y, homeY + wave, 0.1)

    if (sprite.x < homeX - 80) {
      return "focus"
    }
    return null
  }

  private updateFocus(
    context: EnemyStateContext<PiranhaPhase>,
  ): PiranhaPhase | null {
    const {
      sprite,
      enemyData,
      playerY,
      playerX,
      stateTime,
      scrollCompensation,
    } = context
    const spec = this.getStrikeSpec(context)
    if (!spec) return null

    enemyData.vx = scrollCompensation
    enemyData.vy = 0
    // Faster tracking during focus
    sprite.y = Phaser.Math.Linear(sprite.y, playerY, 0.15)

    const chargeDelay = spec.chargeDelayMs ?? 400
    if (stateTime >= chargeDelay) {
      enemyData.piranhaFocusX = playerX
      enemyData.piranhaFocusY = playerY
      return "charge"
    }
    return null
  }

  private updateCharge(
    context: EnemyStateContext<PiranhaPhase>,
  ): PiranhaPhase | null {
    const { sprite, enemyData, delta, scrollCompensation } = context
    const spec = this.getStrikeSpec(context)
    if (!spec) return null

    enemyData.vx = scrollCompensation
    enemyData.vy = 0
    const tx = enemyData.piranhaFocusX ?? sprite.x
    const ty = enemyData.piranhaFocusY ?? sprite.y
    const dx = tx - sprite.x
    const dy = ty - sprite.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist > 20) {
      const speed = spec.chargeSpeed
      const frameScale = delta / 16
      sprite.x += (dx / dist) * speed * frameScale
      sprite.y += (dy / dist) * speed * frameScale
    } else {
      const attackCount = enemyData.piranhaAttackCount ?? 0
      const nextCount = attackCount + 1
      enemyData.piranhaAttackCount = nextCount

      if (nextCount < (spec.attackCount ?? 3)) {
        return "retreat"
      }
      return "exit"
    }
    return null
  }

  private updateRetreat(
    context: EnemyStateContext<PiranhaPhase>,
  ): PiranhaPhase | null {
    const { sprite, enemyData, delta, scrollCompensation, stateTime } = context
    const spec = this.getStrikeSpec(context)
    if (!spec) return null

    const homeX = enemyData.homeX ?? sprite.x
    const homeY = enemyData.homeY ?? sprite.y

    enemyData.vx = scrollCompensation
    enemyData.vy = 0
    sprite.x += spec.retreatSpeed * (delta / 16)
    enemyData.vy = (homeY - sprite.y) * 0.1

    if (stateTime > 600 || sprite.x >= homeX - 20) {
      return "swim"
    }
    return null
  }

  private updateExit(
    context: EnemyStateContext<PiranhaPhase>,
  ): PiranhaPhase | null {
    const { sprite, enemyData, delta, scrollCompensation, time } = context
    const spec = this.getStrikeSpec(context)
    if (!spec) return null

    const homeY = enemyData.homeY ?? sprite.y

    enemyData.vx = scrollCompensation
    enemyData.vy = 0
    sprite.x += spec.retreatSpeed * (delta / 16)
    sprite.y = Phaser.Math.Linear(
      sprite.y,
      homeY + Math.sin(time / 300) * 20,
      0.08,
    )

    return null
  }

  public override onHeroCollision():
    | "damage-only"
    | "damage-and-defeat"
    | null {
    return "damage-and-defeat"
  }
}
